import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import RichTextEditor from "@/components/RichTextEditor";
import { ScheduleCard } from "@/components/ScheduleCard";
import { parseParis, formatParis, parisNow } from "@/lib/parisTime";
import { SearchableSelect } from "@/components/SearchableSelect";
import { 
  ArrowLeft, Save, Send, Loader2, FileText, Clock,
  Globe, X, CheckCircle2, XCircle, FolderOpen, RefreshCw,
  Upload, Link, Image as ImageIcon, User
} from "lucide-react";

export default function ArticleEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const isEditing = !!id;

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [sites, setSites] = useState([]);
  const [siteCategories, setSiteCategories] = useState({});
  const [siteAuthors, setSiteAuthors] = useState({});
  const [loadingCategories, setLoadingCategories] = useState({});
  const [loadingAuthors, setLoadingAuthors] = useState({});
  const [publishResults, setPublishResults] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    content: "",
    excerpt: "",
    categories: [],
    tags: [],
    featured_image: "",
    status: "draft",
    scheduled_date: null,
    site_ids: [],
    authors: {}  // {site_id: author_id}
  });

  const [tagInput, setTagInput] = useState("");
  const [catSearch, setCatSearch] = useState({});

  useEffect(() => {
    fetchSites();
    if (isEditing) {
      fetchArticle();
    }
  }, [id]);

  const fetchSites = async () => {
    try {
      const response = await fetch(`${API}/sites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSites(data);
      }
    } catch (error) {
      console.error("Failed to fetch sites:", error);
    }
  };

  const fetchArticle = async () => {
    try {
      const response = await fetch(`${API}/articles/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFormData({
          ...data,
          scheduled_date: parseParis(data.scheduled_date)
        });
        // Fetch categories for selected sites
        data.site_ids?.forEach(siteId => { fetchSiteCategories(siteId); fetchSiteAuthors(siteId); });
      } else {
        toast.error("Article non trouvé");
        navigate("/articles");
      }
    } catch (error) {
      toast.error("Erreur lors du chargement de l'article");
      navigate("/articles");
    } finally {
      setLoading(false);
    }
  };

  const fetchSiteCategories = async (siteId) => {
    if (siteCategories[siteId]) return; // Already loaded
    
    setLoadingCategories(prev => ({ ...prev, [siteId]: true }));
    
    try {
      const response = await fetch(`${API}/sites/${siteId}/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const categories = await response.json();
        setSiteCategories(prev => ({ ...prev, [siteId]: categories }));
      }
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    } finally {
      setLoadingCategories(prev => ({ ...prev, [siteId]: false }));
    }
  };

  const fetchSiteAuthors = async (siteId) => {
    if (siteAuthors[siteId]) return; // Already loaded
    
    setLoadingAuthors(prev => ({ ...prev, [siteId]: true }));
    
    try {
      const response = await fetch(`${API}/sites/${siteId}/authors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const authors = await response.json();
        setSiteAuthors(prev => ({ ...prev, [siteId]: authors }));
      }
    } catch (error) {
      console.error("Failed to fetch authors:", error);
    } finally {
      setLoadingAuthors(prev => ({ ...prev, [siteId]: false }));
    }
  };

  const handleSave = async ({ silent = false } = {}) => {
    setSaving(true);
    
    try {
      const payload = {
        ...formData,
        status: formData.scheduled_date ? "scheduled" : "draft",
        scheduled_date: formatParis(formData.scheduled_date)
      };

      const url = isEditing ? `${API}/articles/${id}` : `${API}/articles`;
      const method = isEditing ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        if (!silent) toast.success(isEditing ? "Article mis à jour" : "Article créé");
        if (!isEditing) {
          navigate(`/articles/${data.id}`, { replace: true });
        }
        return data.id;
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de l'enregistrement");
      }
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
    return null;
  };

  const handlePublish = async (mode) => {
    if (formData.site_ids.length === 0) {
      toast.error("Sélectionnez au moins un site pour publier");
      return;
    }
    if (mode === "publish" && formData.scheduled_date && formData.scheduled_date <= parisNow()) {
      toast.error("La date de planification doit être dans le futur");
      return;
    }

    setPublishing(mode);
    setPublishResults(null);

    try {
      const articleId = await handleSave({ silent: true });
      if (!articleId) return;

      const response = await fetch(`${API}/articles/${articleId}/publish?mode=${mode}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });

      const results = await response.json();
      setPublishResults(results.results);

      const successCount = results.results.filter(r => r.status === "success").length;
      const errorCount = results.results.filter(r => r.status === "error").length;
      results.results.filter(r => r.image_error).forEach(r => toast.error(`Image non prise en compte sur ${r.site_name} : ${r.image_error}`, { duration: 10000 }));


      const verb = mode === "draft" ? "Brouillon envoyé" : formData.scheduled_date ? "Planifié" : "Publié";
      if (successCount > 0 && errorCount === 0) {
        toast.success(`${verb} sur ${successCount} site(s)`);
      } else if (successCount > 0) {
        toast.warning(`${verb} sur ${successCount} site(s), ${errorCount} échec(s)`);
      } else {
        toast.error("Échec de la publication");
      }
    } catch (error) {
      toast.error("Erreur lors de la publication");
    } finally {
      setPublishing(false);
    }
  };

  const toggleSite = (siteId) => {
    if (formData.site_ids.includes(siteId)) {
      // Remove site
      const newAuthors = { ...formData.authors };
      delete newAuthors[siteId];
      setFormData({ 
        ...formData, 
        site_ids: formData.site_ids.filter(id => id !== siteId),
        categories: formData.categories.filter(c => !c.startsWith(`${siteId}:`)),
        authors: newAuthors
      });
    } else {
      // Add site
      setFormData({ ...formData, site_ids: [...formData.site_ids, siteId] });
      fetchSiteCategories(siteId);
      fetchSiteAuthors(siteId);
    }
  };

  const setAuthorForSite = (siteId, authorId) => {
    setFormData({
      ...formData,
      authors: { ...formData.authors, [siteId]: authorId }
    });
  };

  const toggleCategory = (siteId, categoryId, categoryName) => {
    const catKey = `${siteId}:${categoryId}:${categoryName}`;
    if (formData.categories.includes(catKey)) {
      setFormData({ 
        ...formData, 
        categories: formData.categories.filter(c => c !== catKey) 
      });
    } else {
      setFormData({ 
        ...formData, 
        categories: [...formData.categories, catKey] 
      });
    }
  };

  const isCategorySelected = (siteId, categoryId) => {
    return formData.categories.some(c => c.startsWith(`${siteId}:${categoryId}:`));
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
      setTagInput("");
    }
  };

  const removeTag = (tag) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Veuillez sélectionner une image");
      return;
    }

    setUploading(true);

    try {
      // Use article title as image name, or default
      const imageName = formData.title?.trim() || "image-article";
      
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('name', imageName);

      const response = await fetch(`${API}/media/upload`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formDataUpload
      });

      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, featured_image: data.url }));
        toast.success("Image téléchargée avec succès");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors du téléchargement");
      }
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="editor-loading">
        <div className="animate-pulse text-[#6B7280]">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="article-editor-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/articles")} data-testid="back-btn" className="text-[#1E2A5A] hover:bg-[#F5F7FA]">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold font-heading text-[#1E2A5A]">
              {isEditing ? "Modifier l'article" : "Nouvel article"}
            </h1>
          </div>
        </div>
        <div className="grid grid-cols-3 sm:flex gap-2 sm:gap-3">
          <Button 
            variant="outline" 
            onClick={() => handleSave()}
            disabled={saving || !!publishing}
            data-testid="save-btn"
            className="rounded-xl border-gray-200 text-[#1E2A5A] hover:bg-[#F5F7FA] px-2 sm:px-4 text-xs sm:text-sm"
          >
            {saving && !publishing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Enregistrer
          </Button>
          <Button 
            variant="outline" 
            onClick={() => handlePublish("draft")}
            disabled={saving || !!publishing}
            data-testid="draft-btn"
            className="rounded-xl border-gray-200 text-[#1E2A5A] hover:bg-[#F5F7FA] px-2 sm:px-4 text-xs sm:text-sm"
          >
            {publishing === "draft" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
            Brouillon
          </Button>
          <Button 
            className={`${formData.scheduled_date ? "bg-[#20C997] hover:bg-[#1E2A5A]" : "bg-[#1E2A5A] hover:bg-[#20C997]"} rounded-xl transition-colors px-2 sm:px-4 text-xs sm:text-sm`}
            onClick={() => handlePublish("publish")}
            disabled={saving || !!publishing}
            data-testid="publish-btn"
          >
            {publishing === "publish" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : formData.scheduled_date ? <Clock className="w-4 h-4 mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            {formData.scheduled_date ? "Planifier" : "Publier"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Editor */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="border-0 bg-white rounded-2xl soft-shadow">
            <CardContent className="p-6 space-y-6">
              <div className="space-y-2">
                <Label htmlFor="title" className="text-[#1E2A5A]">Titre</Label>
                <Input
                  id="title"
                  placeholder="Titre de l'article"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="text-lg font-medium h-12 rounded-xl border-gray-200 focus:border-[#20C997] focus:ring-[#20C997]"
                  data-testid="article-title-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[#1E2A5A]">Contenu</Label>
                <RichTextEditor
                  content={formData.content}
                  onChange={(content) => setFormData({ ...formData, content })}
                  placeholder="Écrivez ou collez votre article ici..."
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <ScheduleCard
            value={formData.scheduled_date}
            onChange={(d) => setFormData((prev) => ({ ...prev, scheduled_date: d }))}
          />
          {/* Sites Selection */}
          <Card className="border-0 bg-white rounded-2xl soft-shadow">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-heading text-[#1E2A5A] flex items-center gap-2">
                <Globe className="w-4 h-4" />
                Sites de publication
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {sites.length > 0 ? (
                sites.map((site) => (
                  <div 
                    key={site.id} 
                    className={`p-3 rounded-xl border transition-colors ${
                      formData.site_ids.includes(site.id) 
                        ? 'border-[#20C997] bg-[#20C997]/5' 
                        : 'border-gray-200 hover:bg-[#F5F7FA]'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <Checkbox
                        id={`site-${site.id}`}
                        checked={formData.site_ids.includes(site.id)}
                        onCheckedChange={() => toggleSite(site.id)}
                        data-testid={`site-checkbox-${site.id}`}
                        className="border-gray-300 data-[state=checked]:bg-[#20C997] data-[state=checked]:border-[#20C997]"
                      />
                      <label 
                        htmlFor={`site-${site.id}`}
                        className="flex-1 cursor-pointer"
                      >
                        <p className="font-medium text-sm text-[#1E2A5A]">{site.name}</p>
                        <p className="text-xs text-[#6B7280] truncate">{site.url}</p>
                      </label>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-[#6B7280] text-center py-4">
                  Aucun site connecté
                </p>
              )}
            </CardContent>
          </Card>

          {/* Categories from selected sites */}
          {formData.site_ids.length > 0 && (
            <Card className="border-0 bg-white rounded-2xl soft-shadow">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-heading text-[#1E2A5A] flex items-center gap-2">
                  <FolderOpen className="w-4 h-4" />
                  Catégories
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {formData.site_ids.map((siteId) => {
                    const site = sites.find(s => s.id === siteId);
                    const categories = siteCategories[siteId] || [];
                    const isLoading = loadingCategories[siteId];

                    return (
                      <div key={siteId} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-[#6B7280]">{site?.name}</p>
                          {isLoading && <Loader2 className="w-3 h-3 animate-spin text-[#20C997]" />}
                          {!isLoading && categories.length === 0 && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-6 text-xs text-[#6B7280] hover:text-[#1E2A5A]"
                              onClick={() => {
                                setSiteCategories(prev => ({ ...prev, [siteId]: undefined }));
                                fetchSiteCategories(siteId);
                              }}
                            >
                              <RefreshCw className="w-3 h-3 mr-1" />
                              Recharger
                            </Button>
                          )}
                        </div>
                        
                        {isLoading ? (
                          <div className="text-xs text-[#6B7280] py-2">Chargement...</div>
                        ) : categories.length > 0 ? (
                          <>
                          <Input
                            placeholder="Rechercher une catégorie..."
                            value={catSearch[siteId] || ""}
                            onChange={(e) => setCatSearch((prev) => ({ ...prev, [siteId]: e.target.value }))}
                            data-testid={`editor-category-search-${siteId}`}
                            className="h-9 text-sm rounded-xl border-gray-200 focus:border-[#20C997] focus:ring-[#20C997]"
                          />
                          <div className="max-h-[200px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent hover:scrollbar-thumb-gray-400">
                            <div className="space-y-1">
                              {categories.filter((cat) => cat.name.toLowerCase().includes((catSearch[siteId] || "").toLowerCase())).map((cat) => (
                                <div 
                                  key={cat.id}
                                  className={`flex items-center space-x-2 p-2 rounded-lg transition-colors cursor-pointer ${
                                    isCategorySelected(siteId, cat.id)
                                      ? 'bg-[#20C997]/10'
                                      : 'hover:bg-[#F5F7FA]'
                                  }`}
                                  onClick={() => toggleCategory(siteId, cat.id, cat.name)}
                                >
                                  <Checkbox
                                    checked={isCategorySelected(siteId, cat.id)}
                                    onCheckedChange={() => toggleCategory(siteId, cat.id, cat.name)}
                                    className="border-gray-300 data-[state=checked]:bg-[#20C997] data-[state=checked]:border-[#20C997]"
                                  />
                                  <span className="text-sm flex-1 text-[#1E2A5A]">{cat.name}</span>
                                  <Badge className="text-xs rounded-lg bg-[#F5F7FA] text-[#6B7280] hover:bg-[#F5F7FA]">{cat.count}</Badge>
                                </div>
                              ))}
                            </div>
                          </div>
                          </>
                        ) : (
                          <p className="text-xs text-[#6B7280] py-2">
                            Testez la connexion au site d'abord
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Selected categories summary */}
                {formData.categories.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-xs text-[#6B7280] mb-2">Sélectionnées :</p>
                    <div className="flex flex-wrap gap-1">
                      {formData.categories.map((catKey) => {
                        const [siteId, catId, catName] = catKey.split(':');
                        const site = sites.find(s => s.id === siteId);
                        return (
                          <Badge 
                            key={catKey} 
                            className="gap-1 text-xs rounded-lg bg-[#1E2A5A]/10 text-[#1E2A5A] hover:bg-[#1E2A5A]/10"
                          >
                            {catName}
                            <X 
                              className="w-3 h-3 cursor-pointer hover:text-red-500" 
                              onClick={() => toggleCategory(siteId, catId, catName)}
                            />
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Authors selection */}
          {formData.site_ids.length > 0 && (
            <Card className="border-0 bg-white rounded-2xl soft-shadow">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-heading text-[#1E2A5A] flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Auteurs
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {formData.site_ids.map((siteId) => {
                    const site = sites.find(s => s.id === siteId);
                    const authors = siteAuthors[siteId] || [];
                    const isLoading = loadingAuthors[siteId];
                    const selectedAuthor = formData.authors[siteId];

                    return (
                      <div key={siteId} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-[#6B7280]">{site?.name}</p>
                          {isLoading && <Loader2 className="w-3 h-3 animate-spin text-[#20C997]" />}
                          {!isLoading && authors.length === 0 && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-6 text-xs text-[#6B7280] hover:text-[#1E2A5A]"
                              onClick={() => {
                                setSiteAuthors(prev => ({ ...prev, [siteId]: undefined }));
                                fetchSiteAuthors(siteId);
                              }}
                            >
                              <RefreshCw className="w-3 h-3 mr-1" />
                              Recharger
                            </Button>
                          )}
                        </div>
                        
                        {isLoading ? (
                          <div className="text-xs text-[#6B7280] py-2">Chargement...</div>
                        ) : authors.length > 0 ? (
                          <SearchableSelect
                            testId={`editor-author-select-${siteId}`}
                            placeholder="Sélectionner un auteur"
                            searchPlaceholder="Rechercher un auteur..."
                            className="h-10 text-sm"
                            options={authors.map((a) => ({ value: a.id.toString(), label: a.name }))}
                            value={selectedAuthor?.toString() || ""}
                            onChange={(value) => setAuthorForSite(siteId, value)}
                          />
                        ) : (
                          <p className="text-xs text-[#6B7280] py-2">
                            Aucun auteur disponible
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Publish Results */}
          {publishResults && (
            <Card className="border-0 bg-white rounded-2xl soft-shadow">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-heading text-[#1E2A5A]">Résultats</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {publishResults.map((result, index) => (
                  <div 
                    key={index}
                    className={`flex items-center gap-2 p-3 rounded-xl ${
                      result.status === "success" ? "bg-[#20C997]/10" : "bg-red-500/10"
                    }`}
                  >
                    {result.status === "success" ? (
                      <CheckCircle2 className="w-4 h-4 text-[#20C997]" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-500" />
                    )}
                    <span className="text-sm flex-1 text-[#1E2A5A]">{result.site_name}</span>
                    {result.link && (
                      <a 
                        href={result.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-[#20C997] hover:underline"
                      >
                        Voir
                      </a>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Tags */}
          <Card className="border-0 bg-white rounded-2xl soft-shadow">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-heading text-[#1E2A5A]">Tags</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2">
                <Input
                  placeholder="Ajouter un tag"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
                  data-testid="tag-input"
                  className="rounded-xl border-gray-200 focus:border-[#20C997] focus:ring-[#20C997]"
                />
                <Button variant="outline" size="icon" onClick={addTag} className="rounded-xl border-gray-200">
                  +
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.tags.map((tag) => (
                  <Badge key={tag} className="gap-1 rounded-lg bg-[#1E2A5A]/10 text-[#1E2A5A] hover:bg-[#1E2A5A]/10">
                    {tag}
                    <X 
                      className="w-3 h-3 cursor-pointer hover:text-red-500" 
                      onClick={() => removeTag(tag)}
                    />
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Featured Image */}
          <Card className="border-0 bg-white rounded-2xl soft-shadow">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-heading text-[#1E2A5A] flex items-center gap-2">
                <ImageIcon className="w-4 h-4" />
                Image à la une
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* URL Input */}
              <div className="space-y-2">
                <Label className="text-sm text-[#6B7280] flex items-center gap-2">
                  <Link className="w-3 h-3" />
                  Lien de l'image
                </Label>
                <Input
                  placeholder="https://... ou lien Google"
                  value={formData.featured_image || ""}
                  onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
                  data-testid="featured-image-input"
                  className="rounded-xl border-gray-200 focus:border-[#20C997] focus:ring-[#20C997]"
                />
              </div>

              {/* Separator */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-gray-200"></div>
                <span className="text-xs text-[#6B7280]">ou</span>
                <div className="flex-1 h-px bg-gray-200"></div>
              </div>

              {/* Upload Zone */}
              <div className="space-y-2">
                <Label className="text-sm text-[#6B7280] flex items-center gap-2">
                  <Upload className="w-3 h-3" />
                  Télécharger une image
                </Label>
                <div className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center hover:border-[#20C997]/50 transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="image-upload"
                    disabled={uploading}
                  />
                  <label 
                    htmlFor="image-upload" 
                    className="cursor-pointer flex flex-col items-center gap-2"
                  >
                    {uploading ? (
                      <Loader2 className="w-6 h-6 animate-spin text-[#20C997]" />
                    ) : (
                      <Upload className="w-6 h-6 text-[#6B7280]" />
                    )}
                    <span className="text-xs text-[#6B7280]">
                      {uploading ? "Téléchargement..." : "Cliquez pour sélectionner"}
                    </span>
                  </label>
                </div>
                <p className="text-xs text-[#6B7280]">
                  Conversion auto en JPEG avec le nom : <strong className="text-[#1E2A5A]">{formData.title || "titre-article"}</strong>
                </p>
              </div>

              {/* Image Preview */}
              {formData.featured_image && (
                <div className="relative rounded-xl overflow-hidden border border-gray-200">
                  <img 
                    src={formData.featured_image} 
                    alt="Preview" 
                    className="w-full h-40 object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 right-2 h-6 w-6 rounded-lg"
                    onClick={() => setFormData({ ...formData, featured_image: "" })}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
